import { useState } from 'react';

import { useDispatch } from 'react-redux';

import { Box, Button, Checkbox, Chip, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';

import CheckIcon from '@mui/icons-material/Check';

import moment from 'moment';

import { TablePaginationActions } from '../../../helpers/TablePaginationActions';

import { usePages } from '../../../hooks/usePages';

import { openSnackbar } from '../../../store/slices/SnackbarSlice';

import { TableTheadStyle, TablePaddingStyle } from '../../../styles/TableStyle';

import { DialogDocumento } from '../../../dialogs/DialogDocumento';

import { GuardarPromociones } from '../../../connections/audiencias/AudienciaConnection';

import { AudienciaProps, PromocionesProps } from '../../../interfaces/audiencias/AudienciaInterface';

interface Props {
    audiencia:          AudienciaProps;
    promociones:        PromocionesProps[];
    edit?:              boolean;
    setPromociones?:    React.Dispatch<React.SetStateAction<PromocionesProps[]>>;
}

export const AcordarPromociones = ({ audiencia, edit = true, promociones, setPromociones }: Props ) => {

    const dispatch = useDispatch( )

    const [loading, setLoading] = useState( false );

    const [promocion, setPromocion] = useState( '' );
    const [openPromocion, setOpenPromocion] = useState( false );

    const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePages(5);

    const handleCheckAll = ( checked: boolean ) => {

        if ( !setPromociones ) return;

        setPromociones(promociones.map(row => ({ ...row, acordar: checked })));

    }

    const handleChangeChecked = ( id: number ) => {

        if (!setPromociones) return;

        setPromociones(promociones.map( row => row.id === id ? { ...row, acordar: !row.acordar } : row ));

    };

    const handleGuardarPromociones = async () => {

        setLoading( true );

        await GuardarPromociones({ idAudiencia: audiencia.idAudiencia, promociones }).then(resp => {

            setTimeout(() => {

                const { success, message } = resp;

                if ( success ) {

                    dispatch( openSnackbar({ message: message, variant: 'success' }) );
                    setLoading( false );

                }
                else {

                    dispatch( openSnackbar({ message: message, variant: 'error' }) );
                    setLoading( false );

                }

            }, 700);

        });
    }

    return (
        <Box p={ 4 } component={ Paper }>

            <Grid container spacing={ 3 }>

                <Grid size={{ md: 12, xs: 12 }} >

                    <TableContainer sx={{ borderRadius: 3, mt: 1 }}>

                        <Table sx={{ minWidth: 500 }}>

                            <TableHead sx={{ ...TableTheadStyle }}>

                                <TableRow sx={{ borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>

                                    <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '10%' }}>Número de seguimiento</TableCell>
                                    <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '20%', textAlign: 'center' }}>Estatus</TableCell>
                                    <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '20%' }}>Usuario asignado</TableCell>
                                    <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '15%', textAlign: 'center' }}>Promoción</TableCell>

                                    {
                                        edit
                                        &&
                                            <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '10%', fontWeight: 'bold', textAlign: 'end' }}>

                                                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 1.5 }}>

                                                    <span>Acordar</span>
                                                    {
                                                        edit
                                                        &&
                                                            <Tooltip title='Seleccionar todo'>
                                                                <Checkbox
                                                                    checked={ promociones.filter( ({ acordar }) => acordar === true ).length !== 0 }
                                                                    disabled={ promociones.length === 0 }
                                                                    onChange={ ( e ) => handleCheckAll( e.target.checked ) }
                                                                    sx={{
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        padding: '5px',
                                                                        ':hover': { bgcolor: 'white' }
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                    }

                                                </Box>

                                            </TableCell>
                                    }

                                </TableRow>

                            </TableHead>

                            <TableBody>

                                {
                                    (
                                        rowsPerPage > 0
                                        ? promociones.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        : promociones
                                    ).map(({ noSeguimiento, estatus, usuarioAsignado, fecha, promocion, acordar, id }, index) => (

                                        <TableRow key={ index }>

                                            <TableCell>

                                                <Typography variant='h6'>
                                                    <strong>{ noSeguimiento }</strong>
                                                </Typography>

                                                {
                                                    fecha
                                                    &&
                                                        <>

                                                            <Typography variant='subtitle1' color='text.secondary' mt={ 1 }>
                                                                { moment( fecha ).format( 'DD-MM-YYYY' ) }
                                                            </Typography>

                                                            <Typography variant='caption' color='text.secondary'>
                                                                { moment( fecha ).format( 'hh:mm a' ) }
                                                            </Typography>

                                                        </>
                                                }

                                            </TableCell>

                                            <TableCell sx={{ textAlign: 'center' }}>

                                                <Chip
                                                    variant='filled'
                                                    color='primary'
                                                    sx={{ fontWeight: 'bold', px: 3, py: 3 }}
                                                    label={
                                                        <Typography variant='subtitle1' fontWeight={ '600' } fontSize={ 14 }>
                                                            { estatus }
                                                        </Typography>
                                                    }
                                                />

                                            </TableCell>

                                            <TableCell> { usuarioAsignado } </TableCell>

                                            <TableCell sx={{ textAlign: 'center' }}>

                                                <Button
                                                    fullWidth
                                                    variant='contained'
                                                    sx={{ textTransform: 'none' }}
                                                    onClick={() => { setPromocion( promocion ); setOpenPromocion( true );  }}
                                                >
                                                    Ver
                                                </Button>

                                            </TableCell>

                                            {
                                                edit
                                                &&
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <Checkbox checked={ acordar } onChange={ () => handleChangeChecked( id ) } />
                                                    </TableCell>
                                            }

                                            { acordar && <CheckIcon fontSize='large' /> }

                                        </TableRow>

                                    ))
                                }

                                {
                                    promociones.length === 0
                                    &&
                                        <TableRow>

                                            <TableCell
                                                colSpan={ 7 }
                                                align="center"
                                            >
                                                <Typography variant="caption" color='secondary'> No se encontraron promociones </Typography>

                                            </TableCell>

                                        </TableRow>
                                }

                            </TableBody>

                            <TableFooter>

                                <TableRow>

                                    <TablePagination
                                        rowsPerPageOptions={ [ ] }
                                        colSpan={ 6 }
                                        count={ promociones.length }
                                        rowsPerPage={ rowsPerPage }
                                        page={ page }
                                        onPageChange={ handleChangePage }
                                        onRowsPerPageChange={ handleChangeRowsPerPage }
                                        ActionsComponent={ TablePaginationActions }
                                        labelDisplayedRows={
                                            ({ from, to, count }) => {
                                                return '' + from + '-' + to + ' de ' + count
                                            }
                                        }
                                    />

                                </TableRow>

                            </TableFooter>

                        </Table>

                    </TableContainer>

                </Grid>

                {
                    ( edit && promociones.length !== 0 )
                    &&
                        <Grid size={{ md: 12, xs: 12 }} sx={{ mt: 3 }}>

                            <Grid container spacing={ 3 }>

                                <Grid size={{ md: 4, xs: 12 }} sx={{ textAlign: 'right' }}> </Grid>

                                <Grid size={{ md: 4, xs: 12 }} sx={{ textAlign: 'center' }}>

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        loading={ loading }
                                        onClick={ handleGuardarPromociones }
                                    >
                                        Guardar
                                    </Button>

                                </Grid>

                                <Grid size={{ md: 4, xs: 12 }} style={{ textAlign: 'left' }}> </Grid>

                            </Grid>

                        </Grid>
                }

            </Grid>

            <DialogDocumento
                openDialog={ openPromocion }
                handleCloseDialog={ () => { setOpenPromocion( false ); setPromocion( '' ); } }
                documento={ promocion }
            />

        </Box>
    )
}
